import { ApiExternalEntityStatus, ApiUTMEntity, ApiUTMEntityLevel } from "@incendium/api";

export type EntityType = "campaign" | "adGroup" | "ad";

export interface BaseEntity {
  id: number;
  name: string;
  level: ApiUTMEntityLevel;
  type: EntityType;
  campaignName?: string;
  adGroupName?: string;
  campaignId?: number;
  adGroupId?: number;
  entityStatus?: ApiExternalEntityStatus;
}

export interface CampaignEntity extends BaseEntity {
  type: "campaign";
  currentLandingPathId?: number;
  currentSmartLinkUrl?: string;
  currentSplitCampaignId?: number;
}

export interface AdGroupEntity extends BaseEntity {
  type: "adGroup";
  campaignId: number;
  currentLandingPathId?: number;
  currentSmartLinkUrl?: string;
  currentSplitCampaignId?: number;
}

export interface AdEntity extends BaseEntity {
  type: "ad";
  campaignId?: number;
  adGroupId?: number;
  currentLandingPathId?: number;
  currentSmartLinkUrl?: string;
  currentSplitCampaignId?: number;
}

export type Entity = CampaignEntity | AdGroupEntity | AdEntity;

export interface CampaignGroup {
  campaign: CampaignEntity;
  adGroups: Array<{
    adGroup: AdGroupEntity;
    ads: AdEntity[];
  }>;
}

/**
 * Builds a hierarchical structure of campaigns -> ad groups -> ads from UTM entities
 */
export function buildEntityHierarchy(entities: ApiUTMEntity[]): CampaignGroup[] {
  const groups: CampaignGroup[] = [];
  const seenCampaigns = new Map<number, CampaignEntity>();
  const seenAdGroups = new Map<string, AdGroupEntity>();

  // First pass: collect all campaigns and ad groups
  entities.forEach((entity) => {
    // Add campaign if not already added
    if (entity.externalCampaign?.id && !seenCampaigns.has(entity.externalCampaign.id)) {
      seenCampaigns.set(entity.externalCampaign.id, {
        id: entity.externalCampaign.id,
        name: entity.externalCampaign.name || "",
        level: ApiUTMEntityLevel.CAMPAIGN,
        type: "campaign",
        currentLandingPathId: entity.externalCampaign.landingPathId,
        currentSmartLinkUrl: entity.externalCampaign.smartLinkUrl,
        currentSplitCampaignId: entity.externalCampaign.splitCampaignId,
        entityStatus: entity.entityStatus,
      });
    }

    // Add ad group if not already added
    if (entity.externalAdgroup?.id && entity.externalCampaign?.id) {
      const key = `${entity.externalCampaign.id}-${entity.externalAdgroup.id}`;
      if (!seenAdGroups.has(key)) {
        seenAdGroups.set(key, {
          id: entity.externalAdgroup.id,
          name: entity.externalAdgroup.name || "",
          level: ApiUTMEntityLevel.AD_GROUP,
          type: "adGroup",
          campaignName: entity.externalCampaign?.name,
          campaignId: entity.externalCampaign.id,
          currentLandingPathId: entity.externalAdgroup.landingPathId,
          currentSmartLinkUrl: entity.externalAdgroup.smartLinkUrl,
          currentSplitCampaignId: entity.externalAdgroup.splitCampaignId,
          entityStatus: entity.entityStatus,
        });
      }
    }
  });

  // Second pass: build hierarchical structure
  const campaigns = Array.from(seenCampaigns.values()).sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  campaigns.forEach((campaign) => {
    // Get ad groups for this campaign
    const adGroups = Array.from(seenAdGroups.values())
      .filter((ag) => ag.campaignId === campaign.id)
      .sort((a, b) => a.name.localeCompare(b.name));

    const adGroupData = adGroups.map((adGroup) => {
      // Get ads for this ad group
      const ads = entities
        .filter(
          (e) =>
            e.externalCampaign?.id === campaign.id &&
            e.externalAdgroup?.id === adGroup.id
        )
        .map((entity) => ({
          id: entity.id || 0,
          name: entity.name || "",
          level: ApiUTMEntityLevel.AD,
          type: "ad" as const,
          campaignName: entity.externalCampaign?.name,
          adGroupName: entity.externalAdgroup?.name,
          campaignId: entity.externalCampaign?.id,
          adGroupId: entity.externalAdgroup?.id,
          currentLandingPathId: entity.landingPathId,
          currentSmartLinkUrl: entity.rezonateUrl,
          currentSplitCampaignId: entity.splitCampaignId,
          entityStatus: entity.entityStatus,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      return { adGroup, ads };
    });

    groups.push({ campaign, adGroups: adGroupData });
  });

  return groups;
}

/**
 * Builds a flat array of entities in hierarchical order (campaigns -> ad groups -> ads)
 * Useful for dropdown/autocomplete components
 */
export function buildFlatEntityList(entities: ApiUTMEntity[]): Entity[] {
  const options: Entity[] = [];
  const seenCampaigns = new Map<number, CampaignEntity>();
  const seenAdGroups = new Map<string, AdGroupEntity>();

  // First pass: collect all campaigns and ad groups
  entities.forEach((entity) => {
    // Add campaign option if not already added
    if (entity.externalCampaign?.id && !seenCampaigns.has(entity.externalCampaign.id)) {
      seenCampaigns.set(entity.externalCampaign.id, {
        id: entity.externalCampaign.id,
        name: entity.externalCampaign.name || "",
        level: ApiUTMEntityLevel.CAMPAIGN,
        type: "campaign",
        currentLandingPathId: entity.externalCampaign.landingPathId,
        currentSmartLinkUrl: entity.externalCampaign.smartLinkUrl,
        currentSplitCampaignId: entity.externalCampaign.splitCampaignId,
        entityStatus: entity.entityStatus,
      });
    }

    // Add ad group option if not already added
    if (entity.externalAdgroup?.id && entity.externalCampaign?.id) {
      const key = `${entity.externalCampaign.id}-${entity.externalAdgroup.id}`;
      if (!seenAdGroups.has(key)) {
        seenAdGroups.set(key, {
          id: entity.externalAdgroup.id,
          name: entity.externalAdgroup.name || "",
          level: ApiUTMEntityLevel.AD_GROUP,
          type: "adGroup",
          campaignName: entity.externalCampaign?.name,
          campaignId: entity.externalCampaign.id,
          currentLandingPathId: entity.externalAdgroup.landingPathId,
          currentSmartLinkUrl: entity.externalAdgroup.smartLinkUrl,
          currentSplitCampaignId: entity.externalAdgroup.splitCampaignId,
          entityStatus: entity.entityStatus,
        });
      }
    }
  });

  // Second pass: add campaigns, then their ad groups, then their ads
  const campaigns = Array.from(seenCampaigns.values()).sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  campaigns.forEach((campaign) => {
    // Add campaign
    options.push(campaign);

    // Add ad groups for this campaign
    const adGroups = Array.from(seenAdGroups.values())
      .filter((ag) => ag.campaignId === campaign.id)
      .sort((a, b) => a.name.localeCompare(b.name));

    adGroups.forEach((adGroup) => {
      options.push(adGroup);

      // Add ads for this ad group
      const ads = entities
        .filter(
          (e) =>
            e.externalCampaign?.id === campaign.id &&
            e.externalAdgroup?.id === adGroup.id
        )
        .map((entity) => ({
          id: entity.id || 0,
          name: entity.name || "",
          level: ApiUTMEntityLevel.AD,
          type: "ad" as const,
          campaignName: entity.externalCampaign?.name,
          adGroupName: entity.externalAdgroup?.name,
          campaignId: entity.externalCampaign?.id,
          adGroupId: entity.externalAdgroup?.id,
          currentLandingPathId: entity.landingPathId,
          currentSmartLinkUrl: entity.rezonateUrl,
          currentSplitCampaignId: entity.splitCampaignId,
          entityStatus: entity.entityStatus,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      options.push(...ads);
    });
  });

  return options;
}

